import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import "../faq-store.css";
import { setJsonLd } from "../seo/pageMeta";
import { faqJsonLd } from "../seo/structuredData";

const groups = [
  {
    id: "shopping",
    label: "Shopping & delivery",
    intro: "Find the right part and plan your order.",
    items: [
      {
        question: "Do you sell gaming machine parts and accessories?",
        answer: (
          <>
            Yes. Our catalogue includes bill acceptors, printers, monitors, game
            boards, electronic components, and accessories.{" "}
            <Link to="/products">Browse the shop</Link> or ask our team for help
            finding a part.
          </>
        ),
      },
      {
        question: "How can I check whether a part fits my machine?",
        answer: (
          <>
            Share your machine model, the part number, and clear photos of the
            existing component and its connectors. Our team can help you check
            compatibility before you order.{" "}
            <Link to="/contact">Contact us about a part</Link>.
          </>
        ),
      },
      {
        question: "Can I collect my order or arrange delivery?",
        answer: (
          <>
            Store pickup is free. You can also choose local bearer delivery, a local courier, or international delivery. Charges and availability depend on the option you choose: couriers set their own charges, and international estimates may change.
            Confirm arrangements with our team before collection or dispatch.
          </>
        ),
      },
      {
        question: "Can I view prices in JMD?",
        answer: (
          <>
            Yes. Use the currency selector to switch between USD and an
            estimated JMD equivalent based on the displayed exchange rate.
            Card payments are charged in USD. For a JMD bank transfer, the exact amount and exchange rate are locked when your order is placed. Upload your receipt for staff verification. Bank fees may apply separately.
          </>
        ),
      },
    ],
  },
  {
    id: "machines",
    label: "Gaming machines & leasing",
    intro: "Choose a machine, discuss a build, or explore leasing.",
    items: [
      {
        question: "Can I customise a gaming machine?",
        answer: (
          <>
            Yes. Choose a machine and use{" "}
            <Link to="/build-your-machine">Build your machine</Link> to describe
            your preferred hardware. The game theme stays fixed while you
            specify components. Our team confirms compatibility, availability,
            and pricing before providing a quotation.
          </>
        ),
      },
      {
        question: "Do you offer gaming machine leasing?",
        answer: (
          <>
            Yes. We offer leasing for eligible bar owners and operators. Tell us
            about your location and the machines you are interested in so we can advise whether leasing suits your venue and explain the arrangement.{" "}
            <Link to="/contact">Ask about leasing</Link>.
          </>
        ),
      },
      {
        question: "Are there sign-up or installation fees for leasing?",
        answer: (
          <>
            No. There are no sign-up or installation fees; we cover both. Speak with our team about
            the lease terms and your location before making arrangements.
          </>
        ),
      },
    ],
  },
  {
    id: "support",
    label: "Warranty & technical support",
    intro: "Get help with your machine or an existing purchase.",
    items: [
      {
        question: "What warranty comes with a new gaming machine?",
        answer: (
          <>
            New gaming machines carry a 60-day warranty from the date of delivery. Longer
            manufacturer warranties and statutory rights still apply. See our{" "}
            <Link to="/warranties">full warranty policy</Link> for coverage and
            claim instructions.
          </>
        ),
      },
      {
        question:
          "What should I do if an item arrives damaged or is not working?",
        answer: (
          <>
            Contact us promptly with your order details, a description of the
            problem, and any useful photos. Our team will assess the issue and
            explain the next steps. Read our{" "}
            <Link to="/termscondition">returns and refund terms</Link> for
            details.
          </>
        ),
      },
      {
        question: "Do you provide repairs and maintenance?",
        answer: (
          <>
            Yes. Our technical services include testing, repairs, installation,
            and maintenance for gaming machines and related components. Tell us the model and what’s going wrong so we can assess it.{" "}
            <Link to="/services">Explore our services</Link>.
          </>
        ),
      },
    ],
  },
  {
    id: "visit",
    label: "Visiting & contacting us",
    intro: "Local support from our team in Kingston.",
    items: [
      {
        question: "Where are you located, and when are you open?",
        answer: (
          <>
            Visit Shop 4B, 17 Molynes Road, Kingston, Jamaica. Our opening hours
            are Monday–Saturday, 9:00 am–6:00 pm, Jamaica time. Contact us
            before travelling to confirm availability or arrange pickup.{" "}
            <Link to="/contact">View our contact page</Link>.
          </>
        ),
      },
      {
        question: "How do I speak with customer service?",
        answer: (
          <>
            Call <a href="tel:+18766484163">+1 (876) 648-4163</a> or{" "}
            <a href="tel:+18764482032">+1 (876) 448-2032</a> during opening
            hours, or email{" "}
            <a href="mailto:technogeniousja@gmail.com">
              technogeniousja@gmail.com
            </a>
            . Include your order number or machine model where relevant so we
            can help you more efficiently.
          </>
        ),
      },
    ],
  },
];

export default function FAQsSection() {
  useEffect(() => {
    setJsonLd("faq", faqJsonLd(groups.flatMap((group) => group.items)));
    return () => setJsonLd("faq", null);
  }, []);
  return (
    <main className="faq-page fq-page">
      <section className="fq-hero">
        <div className="fq-wrap fq-hero-grid">
          <div>
            <p className="fq-eyebrow">TECHNOGENIOUS / HELP CENTRE</p>
            <h1>
              Frequently asked
              <br />
              <span>questions.</span>
            </h1>
            <p>
              From choosing the right part to supporting your gaming machine,
              find the answers you need to take the next step.
            </p>
            <a className="fq-button" href="#shopping">
              Explore the answers <span aria-hidden="true">↓</span>
            </a>
          </div>
          <aside className="fq-quick">
            <span className="fq-mark" aria-hidden="true">
              ?
            </span>
            <h2>A real team. Ready to help.</h2>
            <p>
              Have a specific machine, part, or order in mind? Talk it through
              with us.
            </p>
            <a href="tel:+18766484163">
              +1 (876) 648-4163 <span aria-hidden="true">↗</span>
            </a>
            <p className="fq-hours">Mon–Sat · 9:00 am–6:00 pm · Jamaica time</p>
          </aside>
        </div>
      </section>
      <div className="fq-wrap fq-content">
        <nav className="fq-topics" aria-label="FAQ topics">
          <p className="fq-eyebrow">FIND YOUR ANSWER</p>
          {groups.map((group, index) => (
            <a href={`#${group.id}`} key={group.id}>
              <span aria-hidden="true">0{index + 1}</span>
              {group.label}
              <span aria-hidden="true">↗</span>
            </a>
          ))}
          <Link className="fq-topic-contact" to="/contact">
            Still need help? Contact us →
          </Link>
        </nav>
        <div className="fq-answers">
          {groups.map((group, index) => (
            <section
              id={group.id}
              key={group.id}
              className="fq-group"
              aria-labelledby={`${group.id}-title`}
            >
              <div className="fq-group-heading">
                <span aria-hidden="true">0{index + 1}</span>
                <div>
                  <h2 id={`${group.id}-title`}>{group.label}</h2>
                  <p>{group.intro}</p>
                </div>
              </div>
              {group.items.map((item) => (
                <details className="fq-item" key={item.question}>
                  <summary>
                    {item.question}
                    <span className="fq-toggle" aria-hidden="true" />
                  </summary>
                  <div className="fq-answer">
                    <p>{item.answer}</p>
                  </div>
                </details>
              ))}
            </section>
          ))}
        </div>
      </div>
      <section className="fq-wrap fq-bottom" aria-labelledby="fq-help-title">
        <div>
          <p className="fq-eyebrow">LET’S GET IT SORTED</p>
          <h2 id="fq-help-title">Your question deserves a clear answer.</h2>
          <p>
            Send us your machine model, part number, or order details. We’ll
            help you work out the next step.
          </p>
        </div>
        <Link className="fq-button" to="/contact">
          Talk to our team <span aria-hidden="true">↗</span>
        </Link>
      </section>
    </main>
  );
}
